'use client';

import { useEffect, useRef } from 'react';

import { useAuth } from '@/core/auth/client/AuthProvider';
import { mapAuthError } from '@/features/v2/auth/register/actions/mapAuthError';
import { notifyRegisterError } from '@/features/v2/auth/register/actions/notifyRegisterError';
import { persistDeviceFingerprint } from '@/features/v2/auth/register/actions/persistDeviceFingerprint';
import { useAuthStore } from '@/stores/userStore';

const PENDING_GOOGLE_REGISTRATION_KEY = 'pendingGoogleRegistration';
const PENDING_REGISTRATION_TTL_MS = 10 * 60 * 1000;

type PendingGoogleRegistrationData = {
  welcomeBonus?: string;
  country?: string;
  affiliatorId?: string;
  partner?: string;
  visitorId?: string;
  timestamp: number;
};

const completePendingGoogleRegistration = async ({
  pendingRegistration,
  signOut,
}: {
  pendingRegistration: string;
  signOut: () => Promise<void>;
}): Promise<void> => {
  try {
    const registrationData = JSON.parse(pendingRegistration) as PendingGoogleRegistrationData;

    const isRecent = Date.now() - registrationData.timestamp < PENDING_REGISTRATION_TTL_MS;

    if (!isRecent) {
      localStorage.removeItem(PENDING_GOOGLE_REGISTRATION_KEY);
      return;
    }

    console.warn('[PendingGoogleRegistration] Completing social registration with pending data:', registrationData);

    const response = await fetch('/api/auth/complete-social-registration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        welcomeBonus: registrationData.welcomeBonus,
        country: registrationData.country,
        affiliatorId: registrationData.affiliatorId,
        partner: registrationData.partner,
        visitorId: registrationData.visitorId,
      }),
    });

    const completionResult = await response.json();
    console.warn('[PendingGoogleRegistration] Social registration completion result:', completionResult);

    localStorage.removeItem(PENDING_GOOGLE_REGISTRATION_KEY);

    if (!response.ok || completionResult.success === false) {
      notifyRegisterError(
        mapAuthError(
          completionResult.error || 'Failed to complete registration',
          completionResult.code,
        ),
      );
      await signOut();
      return;
    }

    if (completionResult.success && completionResult.isNewUser) {
      persistDeviceFingerprint({
        visitorId: registrationData.visitorId || null,
      });
      useAuthStore.getState().setTypeBonusSelected(registrationData.welcomeBonus || '');
      window.location.href = '/onboarding';
    }
  } catch (parseError) {
    console.warn('[PendingGoogleRegistration] Error processing pending registration:', parseError);
    localStorage.removeItem(PENDING_GOOGLE_REGISTRATION_KEY);
  }
};

/**
 * Completes Google registration after OAuth redirect by consuming
 * pendingGoogleRegistration from localStorage. Must run only after
 * AuthProvider has finished bootstrapping the session.
 */
export const usePendingGoogleRegistrationCompletion = (): void => {
  const { session, loading, signOut } = useAuth();
  const consumedRef = useRef(false);

  useEffect(() => {
    if (loading || consumedRef.current || !session?.user?.id) return;

    const pendingRegistration = localStorage.getItem(PENDING_GOOGLE_REGISTRATION_KEY);
    if (!pendingRegistration) return;

    // Guard: run at most once per completed bootstrap for this mount.
    consumedRef.current = true;

    void completePendingGoogleRegistration({ pendingRegistration, signOut });
  }, [loading, session, signOut]);
};
